/* eslint-disable @typescript-eslint/no-explicit-any */
import { CSSProperties, memo, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { getTypographyClassaname } from '@components/typography';

import { Marquee } from '@/components/magicui/marquee';
import { cn } from '@/lib/utils';

import { hardwareAcceleration, useIntersectionObserver } from '../../utils/performanceUtils';

const ReviewCard = memo(({ img, name, body }: { img: string; name: string; body: string }) => {
  const cardStyle = useMemo<CSSProperties>(
    () => ({
      ...hardwareAcceleration,
      contain: 'layout style paint',
    }),
    []
  );

  return (
    <figure
      className={cn(
        'rcursor-default elative h-full w-[200px] cursor-pointer overflow-hidden rounded-xl border p-4',
        // light styles
        'border-gray-950/[.1] bg-white hover:bg-gray-950/[.05]',
        // dark styles
        'dark:border-gray-50/[.1] dark:bg-gray-50/[.10] dark:hover:bg-gray-50/[.15] cursor-default'
      )}
      style={cardStyle}
    >
      <div className="cursor-default flex flex-row items-center gap-2">
        <img
          loading="lazy"
          className="cursor-default rounded-full! object-cover w-[32px]! h-[32px]!"
          alt="Profile picture"
          src={img}
          decoding="async"
          width={32}
          height={32}
        />
        <div className="cursor-default flex flex-col">
          <figcaption className="cursor-default text-sm font-medium dark:text-white">{name}</figcaption>
        </div>
      </div>
      <blockquote className={cn('mt-2 cursor-default', getTypographyClassaname('caption', 'start'))}>
        {body}
      </blockquote>
    </figure>
  );
});

ReviewCard.displayName = 'ReviewCard';

// Optimized column renderer component
const MarqueeColumn = memo(
  ({
    reviews,
    columnIndex,
    isVisible,
  }: {
    reviews: Array<{ name: string; body: string; img: string }>;
    columnIndex: number;
    isVisible: boolean;
  }) => {
    const isReverse = columnIndex % 2 === 1;

    if (!isVisible) {
      return <div className="w-[216px]" />; // Placeholder to maintain layout
    }

    return (
      <Marquee reverse={isReverse} className="[--duration:90s]" vertical>
        {reviews.map((review, index) => (
          <ReviewCard key={`review-${columnIndex}-${index}`} {...review} />
        ))}
      </Marquee>
    );
  }
);

MarqueeColumn.displayName = 'MarqueeColumn';

export const Marquee3D = memo(function Marquee3D() {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const isVisible = useIntersectionObserver(containerRef as any, { threshold: 0.1 });

  const reviews = [
    {
      name: 'María Park',
      body: t('mariaGonzalez'),
      img: 'https://avatars.githubusercontent.com/u/103203631',
    },
    {
      name: 'Carlos Mendoza',
      body: t('carlosMendoza'),
      img: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8YXZhdGFyfGVufDB8fDB8fHww&auto=format&fit=crop&w=800&q=60',
    },
    {
      name: 'Isabella Ramos',
      body: t('isabellaRamos'),
      img: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NXx8YXZhdGFyfGVufDB8fDB8fHww&auto=format&fit=crop&w=800&q=60',
    },
    {
      name: 'Diego Herrera',
      body: t('diegoHerrera'),
      img: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=3540&q=80',
    },
    {
      name: 'Valentina Torres',
      body: t('valentinaTorres'),
      img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTB8fGF2YXRhcnxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=800&q=60',
    },
    {
      name: 'Rafael Silva',
      body: t('rafaelSilva'),
      img: 'https://avatars.githubusercontent.com/u/16860528',
    },
    {
      name: 'Sofía Jiménez',
      body: t('sofiaJimenez'),
      img: 'https://images.unsplash.com/photo-1544725176-7c40e5a71c5e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=3534&q=80',
    },
    {
      name: 'Miguel Vargas',
      body: t('miguelVargas'),
      img: 'https://avatars.githubusercontent.com/u/106103610',
    },
    {
      name: 'Camilo Fernández',
      body: t('camilaFernandez'),
      img: 'https://avatars.githubusercontent.com/u/103303671',
    },
    {
      name: 'Antonio López',
      body: t('antonioLopez'),
      img: 'https://avatars.githubusercontent.com/u/20110627',
    },
    {
      name: 'Lucia Morales',
      body: t('luciaMorales'),
      img: 'https://avatars.githubusercontent.com/u/106103615',
    },
    {
      name: 'Eduardo Castillo',
      body: t('eduardoCastillo'),
      img: 'https://avatars.githubusercontent.com/u/89768406',
    },
  ];

  // Memoize reviews array to prevent recalculation
  const memoizedReviews = useMemo(() => reviews, []);

  // Reduce number of columns for better performance
  const columnCount = 8; // Reduced from 12
  const visibleColumns = useMemo(() => {
    if (!isVisible) return [];
    // Only render visible columns when in viewport
    return Array.from({ length: columnCount }, (_, i) => i);
  }, [isVisible]);

  const marqueeColumns = useMemo(() => {
    return Array.from({ length: columnCount }, (_, i) => (
      <MarqueeColumn
        key={i}
        reviews={memoizedReviews}
        columnIndex={i}
        isVisible={visibleColumns.includes(i)}
      />
    ));
  }, [memoizedReviews, visibleColumns]);

  const containerStyle = useMemo<CSSProperties>(
    () => ({
      ...hardwareAcceleration,
      transform: 'translate3d(-100px, 0px, -100px) rotateX(20deg) rotateY(-10deg) rotateZ(20deg)',
      transformStyle: 'preserve-3d',
      contain: 'layout',
    }),
    []
  );

  const gradientStyle = useMemo<CSSProperties>(
    () => ({
      ...hardwareAcceleration,
      contain: 'layout style paint',
    }),
    []
  );

  return (
    <div
      ref={containerRef}
      className="relative flex h-96 w-full flex-row items-center justify-center gap-4 overflow-hidden [perspective:700px]"
      style={{ contain: 'layout style' }}
    >
      <div className="flex flex-row items-center gap-4" style={containerStyle}>
        {marqueeColumns}
      </div>
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-[#fafafa]"
        style={gradientStyle}
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-[#fafafa]"
        style={gradientStyle}
      />
      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-[#fafafa]"
        style={gradientStyle}
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-1/4 bg-gradient-to-l from-[#fafafa]"
        style={gradientStyle}
      />
    </div>
  );
});
